import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, ScrollView, Linking } from 'react-native';
import { theme } from '../../shared/theme/theme';
import { JobsRepository, ServiceRow } from '../../infrastructure/repositories/JobsRepository';
import { useAuth } from '../../infrastructure/auth/AuthContext';
import { LegalGuard } from '../components/LegalGuard';
import { ContractEngine } from '../../domain/use-cases/ContractEngine';

// Prague city center — used when browser/device location isn't available or denied.
const FALLBACK_LOCATION = { latitude: 50.0755, longitude: 14.4378 };

type Step = 1 | 2 | 3 | 4 | 5;

const getCurrentPosition = (): Promise<{ latitude: number; longitude: number }> =>
  new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(FALLBACK_LOCATION);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(FALLBACK_LOCATION),
      { timeout: 5000 },
    );
  });

export const QuickOrderScreen = () => {
  const { session } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [selectedService, setSelectedService] = useState<ServiceRow | null>(null);
  const [matchResult, setMatchResult] = useState<{ fullName: string; rating: number } | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  const [contractSigned, setContractSigned] = useState(false);

  useEffect(() => {
    JobsRepository.listServices().then(setServices).catch((e) => setError(e.message));
  }, []);

  const selectService = (service: ServiceRow) => {
    setSelectedService(service);
    setStep(2);
  };

  const handleOrder = async () => {
    if (!session || !selectedService) return;
    setLoading(true);
    setError(null);
    try {
      const { latitude, longitude } = await getCurrentPosition();
      const jobId = await JobsRepository.createJob(session.user.id, selectedService.id, latitude, longitude);

      const match = await JobsRepository.assignBestHomee(jobId);
      if (!match) {
        setError('Momentálně nejsou v okolí volní žádní ověření HoMees. Zkuste to prosím za chvíli.');
        setLoading(false);
        return;
      }

      setMatchResult({ fullName: match.full_name, rating: match.rating });
      setActiveJobId(jobId);
      setStep(3);
    } catch (e: any) {
      setError(e.message ?? 'Něco se pokazilo.');
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!activeJobId) return;
    setPayLoading(true);
    setError(null);
    try {
      const url = await JobsRepository.createCheckoutUrl(activeJobId);
      await Linking.openURL(url);
      setStep(4);
    } catch (e: any) {
      setError(e.message ?? 'Platbu se nepodařilo zahájit.');
    } finally {
      setPayLoading(false);
    }
  };

  const handleSignContract = async () => {
    if (!activeJobId) return;
    try {
      await ContractEngine.activateJobProtocol(activeJobId);
      setContractSigned(true);
    } catch (e: any) {
      setError(e.message ?? 'Smlouvu se nepodařilo podepsat.');
    }
  };

  const reset = () => {
    setSelectedService(null);
    setMatchResult(null);
    setActiveJobId(null);
    setContractSigned(false);
    setError(null);
    setStep(1);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.header}>HoMee</Text>

      {step === 1 && (
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={styles.title}>Co potřebujete?</Text>
          {services.length === 0 && !error && <ActivityIndicator color={theme.colors.accent} />}
          {services.map((service) => (
            <TouchableOpacity key={service.id} style={styles.option} onPress={() => selectService(service)}>
              <Text style={styles.optionText}>{service.segment} · {service.type}</Text>
              <Text style={styles.optionPrice}>{service.price_per_hour} Kč / h</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {step === 2 && selectedService && (
        <View style={styles.content}>
          <Text style={styles.title}>{selectedService.segment}</Text>
          <Text style={styles.subtitle}>{selectedService.price_per_hour} Kč / h · poloha bude zjištěna automaticky</Text>
          <TouchableOpacity
            style={[styles.confirmButton, { backgroundColor: theme.colors.accent }]}
            onPress={handleOrder}
            disabled={loading}
          >
            {loading
              ? <ActivityIndicator color="#000" />
              : <Text style={styles.confirmText}>OBJEDNAT OKAMŽITĚ</Text>}
          </TouchableOpacity>
          <TouchableOpacity onPress={reset}><Text style={styles.backText}>Zpět</Text></TouchableOpacity>
        </View>
      )}

      {step === 3 && matchResult && !contractSigned && (
        <View style={styles.content}>
          <Text style={styles.title}>HoMee nalezen!</Text>
          <Text style={styles.subtitle}>{matchResult.fullName} ({matchResult.rating.toFixed(1)}★) je na cestě.</Text>
          <LegalGuard
            homeeName={matchResult.fullName}
            onConfirm={handleSignContract}
            onCancel={reset}
          />
        </View>
      )}

      {step === 3 && matchResult && contractSigned && (
        <View style={styles.content}>
          <Text style={styles.title}>Smlouva podepsána</Text>
          <Text style={styles.subtitle}>Pojistné krytí aktivní. Zbývá dokončit platbu.</Text>
          <TouchableOpacity
            style={[styles.confirmButton, { backgroundColor: theme.colors.accent }]}
            onPress={handlePay}
            disabled={payLoading}
          >
            {payLoading
              ? <ActivityIndicator color="#000" />
              : <Text style={styles.confirmText}>ZAPLATIT (STRIPE)</Text>}
          </TouchableOpacity>
        </View>
      )}

      {step === 4 && (
        <View style={styles.content}>
          <Text style={styles.title}>Platba zahájena</Text>
          <Text style={styles.subtitle}>Dokončete platbu v otevřené záložce. Peníze zůstanou v Escrow, dokud zakázku nepotvrdíte jako hotovou.</Text>
          <TouchableOpacity style={[styles.confirmButton, { backgroundColor: theme.colors.accent }]} onPress={reset}>
            <Text style={styles.confirmText}>NOVÁ OBJEDNÁVKA</Text>
          </TouchableOpacity>
        </View>
      )}

      {error && <Text style={styles.error}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
    padding: 30,
    paddingTop: 90,
  },
  header: {
    color: theme.colors.accent,
    fontSize: 24,
    fontWeight: '900',
    letterSpacing: 4,
    position: 'absolute',
    top: 50,
    left: 30,
  },
  content: {
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  title: {
    color: theme.colors.text.main,
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 30,
    textAlign: 'center',
  },
  subtitle: {
    color: theme.colors.text.muted,
    fontSize: 16,
    marginBottom: 40,
    textAlign: 'center',
  },
  option: {
    width: '100%',
    minHeight: 70,
    borderWidth: 1,
    borderColor: theme.colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    marginBottom: 14,
    padding: 12,
  },
  optionText: {
    color: theme.colors.text.main,
    fontSize: 15,
    fontWeight: '900',
    letterSpacing: 1,
  },
  optionPrice: {
    color: theme.colors.accent,
    fontSize: 13,
    marginTop: 4,
    fontWeight: '700',
  },
  confirmButton: {
    width: '100%',
    maxWidth: 380,
    height: 60,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
  },
  confirmText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
    letterSpacing: 1,
  },
  backText: {
    color: theme.colors.accent,
    marginTop: 20,
    textAlign: 'center',
  },
  error: {
    color: '#D32F2F',
    textAlign: 'center',
    marginTop: 10,
  },
});
